/**
 * `/ai/studio` — generate-and-review screen (#19 AC #6).
 *
 * Layout:
 *
 *   Left column   — prompt input (textarea), site selector, target
 *                   ContentItem selector (queries the existing
 *                   `GET /api/admin/sites/:siteId/content-items`),
 *                   generation type selector, "Generate" button.
 *
 *   Right column  — output preview pane. Before accept the 5 judge
 *                   scores render as `—` with a hint ("Accept the draft
 *                   to run judges"); after accept they live on the new
 *                   ContentVersion's edit screen.
 *
 *   Action row    — Accept / Reject / Revise.
 *                     Accept  → calls `/accept`. On success we navigate
 *                               to the Sites detail (the closest
 *                               existing edit surface — admin shell from
 *                               PR #41 doesn't yet expose a content-item
 *                               edit screen) AND show a success toast
 *                               with the new version id.
 *                     Reject  → calls `/reject`. Resets the form to a
 *                               fresh prompt.
 *                     Revise  → re-issues `/generate` with the current
 *                               output as `options.previousOutput`. The
 *                               operator can keep iterating without
 *                               losing their original prompt.
 *
 * Responsive:
 *   - 360 px:    stacked, prompt above output, action buttons full width.
 *   - 768 px+:   two-column layout via MUI `<Stack direction>`.
 *   - 1280 px+:  same layout, slightly larger gaps.
 *
 * MUI only — no Tailwind, CSS modules, or raw CSS.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "@tanstack/react-router";
import type { AiStudioGenerateResponse } from "@ottopilot/shared";
import { useSites } from "../hooks/useSites";
import { useContentItemsForSite } from "../hooks/useContentItems";
import { useAcceptGeneration, useGenerate, useRejectGeneration } from "../hooks/useAiStudio";
import { useToast } from "../components/ToastProvider";
import { BudgetBanner } from "../features/ai/BudgetBanner";
import { ApiClientError } from "../lib/api";

const JUDGE_LABELS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "accuracy", label: "Accuracy" },
  { key: "readability", label: "Readability" },
  { key: "ai_slop", label: "AI Slop" },
  { key: "seo", label: "SEO" },
  { key: "brand_tone", label: "Brand tone" },
];

export function AiStudioPage(): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();

  // Form state (local UI). The current generation lives in
  // `currentGeneration` and is the single source of truth for the
  // preview pane / action buttons. Reset on Reject; replaced on Generate
  // / Revise.
  const [prompt, setPrompt] = useState("");
  const [siteId, setSiteId] = useState<string>("");
  const [contentItemId, setContentItemId] = useState<string>("");
  const [currentGeneration, setCurrentGeneration] = useState<AiStudioGenerateResponse | null>(null);

  const sites = useSites({ page: 1, pageSize: 100 });
  const contentItems = useContentItemsForSite(siteId || undefined, { page: 1, pageSize: 100 });
  const generate = useGenerate();
  const accept = useAcceptGeneration();
  const reject = useRejectGeneration();

  const generateError = generate.error instanceof ApiClientError ? generate.error : null;

  const handleGenerate = () => {
    if (!siteId) {
      toast.showError("Pick a site before generating.");
      return;
    }
    if (prompt.trim().length === 0) {
      toast.showError("Prompt cannot be empty.");
      return;
    }
    generate.mutate(
      {
        siteId,
        contentItemId,
        prompt: prompt.trim(),
      },
      {
        onSuccess: (data) => {
          setCurrentGeneration(data);
        },
        onError: (err) => {
          if (err.code === "AI_BUDGET_EXCEEDED") {
            toast.showError("Monthly AI budget exceeded. Use the override banner to continue.");
            return;
          }
          if (err.code === "AI_RATE_LIMITED") {
            toast.showError("AI request rate limit hit. Wait a moment and try again.");
            return;
          }
          toast.showError(err.message || "Generation failed.");
        },
      },
    );
  };

  const handleRevise = () => {
    if (!currentGeneration) return;
    if (!siteId) {
      toast.showError("Pick a site before generating.");
      return;
    }
    if (prompt.trim().length === 0) {
      toast.showError("Prompt cannot be empty.");
      return;
    }
    generate.mutate(
      {
        siteId,
        contentItemId,
        prompt: prompt.trim(),
        options: { previousOutput: currentGeneration.output },
      },
      {
        onSuccess: (data) => {
          setCurrentGeneration(data);
          toast.showSuccess("Revised draft ready.");
        },
        onError: (err) => toast.showError(err.message || "Revision failed."),
      },
    );
  };

  const handleAccept = () => {
    if (!currentGeneration) return;
    // contentItemId is always present at this point — the Generate button
    // is disabled until the operator picks one, and every generation
    // snapshots it on AiGeneration.inputJson. No defensive re-check.
    accept.mutate(
      { generationId: currentGeneration.generationId },
      {
        onSuccess: (data) => {
          toast.showSuccess(
            data.created
              ? "Draft accepted. Open it to review judge scores and Publish to make it live."
              : "Draft already accepted — opening the existing version.",
          );
          // Land on the new ContentItem detail page (#204) so the operator
          // sees the new DRAFT slot they just created, can read the
          // contentJson, and can hit Publish. Falls back to clearing the
          // generation state when we somehow lack a contentItemId (should
          // be unreachable now that contentItemId is required end-to-end).
          if (siteId && data.contentItemId) {
            void navigate({
              to: "/sites/$siteId/content/$contentItemId",
              params: { siteId, contentItemId: data.contentItemId },
            });
          } else {
            setCurrentGeneration(null);
          }
        },
        onError: (err) => toast.showError(err.message || "Accept failed."),
      },
    );
  };

  const handleReject = () => {
    if (!currentGeneration) return;
    reject.mutate(
      { generationId: currentGeneration.generationId },
      {
        onSuccess: () => {
          toast.showSuccess("Draft rejected.");
          setCurrentGeneration(null);
          setPrompt("");
        },
        onError: (err) => toast.showError(err.message || "Reject failed."),
      },
    );
  };

  const siteOptions = useMemo(() => sites.data?.data ?? [], [sites.data]);
  const itemOptions = useMemo(() => contentItems.data?.data ?? [], [contentItems.data]);
  const selectedContentItem = useMemo(
    () => itemOptions.find((i) => i.id === contentItemId) ?? null,
    [itemOptions, contentItemId],
  );

  const busy = generate.isPending || accept.isPending || reject.isPending;

  return (
    <Stack spacing={3} data-testid="ai-studio-page">
      <Box>
        <Typography variant="h2">AI Studio</Typography>
        <Typography variant="body2" color="text.secondary">
          Draft, review, and accept AI-generated content. Each accept creates a new draft version
          and runs the 5 quality judges.
        </Typography>
      </Box>

      <BudgetBanner />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems="stretch"
        sx={{ width: "100%" }}
      >
        {/* Left column — prompt input */}
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 }, flex: 1, minWidth: 0 }}
          data-testid="ai-studio-prompt-panel"
        >
          <Stack spacing={2}>
            <Typography variant="h3">Prompt</Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="ai-studio-site-label">Site</InputLabel>
              <Select
                labelId="ai-studio-site-label"
                label="Site"
                value={siteId}
                onChange={(e) => {
                  setSiteId(e.target.value);
                  setContentItemId("");
                }}
                disabled={sites.isPending || busy}
                data-testid="ai-studio-site-select"
              >
                {siteOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" required>
              <InputLabel id="ai-studio-item-label">Target content item</InputLabel>
              <Select
                labelId="ai-studio-item-label"
                label="Target content item"
                value={contentItemId}
                onChange={(e) => setContentItemId(e.target.value)}
                disabled={!siteId || contentItems.isPending || busy}
                data-testid="ai-studio-item-select"
              >
                {itemOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {/*
                     * Suffix the ContentItem `type` plus the optional
                     * `blockType` so the operator can visually pick the
                     * right item without opening each one. Many sites
                     * carry several `PAGE_BLOCK` items that differ only
                     * by `blockType` (HERO vs FEATURE_GRID vs FAQ); the
                     * dropdown is otherwise indistinguishable.
                     */}
                    {item.name} ({item.type}
                    {item.blockType ? ` · ${item.blockType}` : ""})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/*
             * Read-only echo of the selected ContentItem's type +
             * blockType — surfaces what the server will use to pick a
             * per-type system-prompt prefix and to tag the AiGeneration
             * row for the Usage dashboard. Operator can't override either
             * here; both are derived from the ContentItem itself (CRUD
             * lives on the Content tab of the site detail page).
             */}
            {selectedContentItem !== null ? (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                data-testid="ai-studio-item-type-chips"
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Type: ${selectedContentItem.type}`}
                  data-testid="ai-studio-item-type-chip"
                />
                {selectedContentItem.blockType ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Block: ${selectedContentItem.blockType}`}
                    data-testid="ai-studio-item-block-chip"
                  />
                ) : null}
              </Stack>
            ) : null}

            <TextField
              label="Prompt"
              multiline
              minRows={6}
              maxRows={20}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want the model to write…"
              disabled={busy}
              fullWidth
              data-testid="ai-studio-prompt-input"
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                onClick={handleGenerate}
                disabled={busy || !siteId || prompt.trim().length === 0}
                data-testid="ai-studio-generate-button"
              >
                {generate.isPending ? "Generating…" : "Generate"}
              </Button>
            </Stack>
            {generateError ? (
              <Alert severity="error" data-testid="ai-studio-error">
                {generateError.message}
              </Alert>
            ) : null}
          </Stack>
        </Paper>

        {/* Right column — output + judges */}
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 }, flex: 1, minWidth: 0 }}
          data-testid="ai-studio-output-panel"
        >
          <Stack spacing={2}>
            <Typography variant="h3">Preview</Typography>

            {generate.isPending && !currentGeneration ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : null}

            {!currentGeneration && !generate.isPending ? (
              <Typography variant="body2" color="text.secondary">
                Output appears here after you click Generate.
              </Typography>
            ) : null}

            {currentGeneration ? (
              <>
                <Card variant="outlined" data-testid="ai-studio-output-card">
                  <CardContent>
                    <Typography
                      variant="body1"
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        m: 0,
                        fontFamily: "inherit",
                      }}
                      data-testid="ai-studio-output-text"
                    >
                      {currentGeneration.output}
                    </Typography>
                  </CardContent>
                </Card>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Quality judges
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept the draft to run all 5 judges. Their scores attach to the new
                    ContentVersion.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                    data-testid="ai-studio-judges-placeholder"
                  >
                    {JUDGE_LABELS.map((j) => (
                      <Box
                        key={j.key}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          minWidth: 100,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {j.label}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 500 }}>
                          —
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Model: {currentGeneration.model} ·{" "}
                    {currentGeneration.tokensIn + currentGeneration.tokensOut} tokens · $
                    {currentGeneration.costUsd.toFixed(4)}
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="flex-end"
                >
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleRevise}
                    disabled={busy}
                    data-testid="ai-studio-revise-button"
                  >
                    Revise
                  </Button>
                  <Button
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={handleReject}
                    disabled={busy}
                    data-testid="ai-studio-reject-button"
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={handleAccept}
                    disabled={busy || !contentItemId}
                    data-testid="ai-studio-accept-button"
                  >
                    Accept
                  </Button>
                </Stack>
              </>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}
